import { TC_DEMO, FECHA_TC_DEMO } from './preview-data';

/**
 * El EDT para el modo vista previa.
 *
 * Guarda en memoria mientras dure la pestaña, como las certificaciones y las
 * provisiones: sin eso no se puede probar lo único que importa, que es que el
 * árbol se conserve.
 *
 * El número y el importe de los padres se **derivan al leer**, igual que en el
 * backend: guardarlos sería un segundo lugar donde pueden decir otra cosa.
 */
interface NodoDemo {
  id: number;
  descripcion: string;
  tipo: string;
  componente: number | null;
  presupuesto: number;
  real: number;
  /* Una linea dictada por una orden de venta de ARMUS: se ve y no se toca.
     Va en la muestra porque el candado hay que poder mirarlo. */
  ordenVenta?: string | null;
  children: NodoDemo[];
}

let siguienteId = 100;

function nodo(
  descripcion: string, presupuesto: number, real: number, children: NodoDemo[] = [],
  tipo = '', componente: number | null = null, ordenVenta: string | null = null
): NodoDemo {
  return { id: siguienteId++, descripcion, tipo, componente, presupuesto, real, ordenVenta, children };
}

let arbol: NodoDemo[] = [
  /* Las ventas cuelgan de un agrupador y cada orden es una hoja debajo. El
     agrupador **no** viene bloqueado: no lo dicta ninguna orden, es
     estructura. Lo que no se toca son las hojas. */
  nodo('Ventas', 0, 0, [
    nodo('Venta Inicial', 480000, 0, [], 'ingreso', null, 'S00099'),
    nodo('Venta S00087', 180000, 0, [], 'ingreso', null, 'S00087')
  ]),
  nodo('Horas de trabajo', 300000, 150000, [
    nodo('Análisis funcional', 120000, 60000, [], 'gasto'),
    nodo('Desarrollo', 180000, 90000, [], 'gasto')
  ]),
  nodo('Viáticos', 150000, 150000, [], 'gasto'),
  /* Este queda **sin tipo a proposito**: es el caso de las 543 lineas de
     produccion, y hay que poder verlo en la muestra. */
  nodo('Otros', 300000, 200000)
];

/* Los ids de las hojas, para que las provisiones de muestra apunten al árbol
   de verdad y no a un código inventado. Se derivan del árbol: escribirlos a
   mano sería el mismo error que la columna «EDT-001» venía a mostrar. */
export const HOJAS_DEMO = {
  analisis: arbol[1].children[0].id,
  desarrollo: arbol[1].children[1].id,
  viaticos: arbol[2].id,
  otros: arbol[3].id
};

/** Con su numeración y sus importes resueltos, como los devuelve el backend */
function comoRespuesta(nodos: NodoDemo[], prefijo = ''): unknown[] {
  return nodos.map((n, i) => {
    const numero = prefijo === '' ? String(i + 1) : `${prefijo}.${i + 1}`;
    const hijos = comoRespuesta(n.children, numero) as Array<{ presupuesto: number; real: number }>;

    return {
      id: n.id,
      numero,
      descripcion: n.descripcion,
      // Un elemento con hijos vale lo que suman sus hijos
      presupuesto: hijos.length ? hijos.reduce((t, h) => t + h.presupuesto, 0) : n.presupuesto,
      real: hijos.length ? hijos.reduce((t, h) => t + h.real, 0) : n.real,
      tipo: n.tipo,
      cost_component_odoo_id: n.componente,
      armus_linea_id: null,
      /* El candado solo en las hojas: un padre agrupa y no lo dicta nadie */
      bloqueado: hijos.length === 0 && !!n.ordenVenta,
      origen: n.ordenVenta ? 'orden_venta' : null,
      orden_venta: hijos.length === 0 ? (n.ordenVenta ?? null) : null,
      presupuesto_propio: n.presupuesto,
      real_propio: n.real,
      // Solo una hoja recibe imputaciones
      imputable: hijos.length === 0,
      children: hijos
    };
  });
}

function desdeElCuerpo(items: any[]): NodoDemo[] {
  return (items ?? []).map((i) => ({
    // Las filas nuevas traen un id inventado por la pantalla ('n7')
    id: Number.isFinite(Number(i?.id)) ? Number(i.id) : siguienteId++,
    descripcion: String(i?.descripcion ?? ''),
    tipo: String(i?.tipo ?? ''),
    componente: i?.cost_component_odoo_id ?? null,
    presupuesto: Number(i?.presupuesto ?? 0),
    real: Number(i?.real ?? 0),
    children: desdeElCuerpo(i?.children ?? [])
  }));
}

function profundidad(nodos: NodoDemo[]): number {
  return nodos.length === 0 ? 0 : 1 + Math.max(...nodos.map((n) => profundidad(n.children)));
}

/* Lo que vale un dólar —la moneda del proyecto de muestra— en otras: las
   mismas del selector de la vista previa, para probar el EDT mirado en otra
   moneda (27/10/2026). */
const POR_DOLAR: Record<string, { factor: number; fecha: string }> = Object.fromEntries(
  Object.entries(TC_DEMO).filter(([c]) => c !== 'USD').map(([c, f]) => [c, { factor: f, fecha: FECHA_TC_DEMO }])
);

/* Lo que contemplan las órdenes de venta de muestra como costo */
const COSTO_PREVISTO = 900000;

/** El árbol en otra moneda, con el propio original al lado, como el backend */
function convertida(nodos: any[], factor: number): any[] {
  return nodos.map((n) => ({
    ...n,
    presupuesto_propio_moneda_proyecto: n.presupuesto_propio,
    presupuesto: Math.round(n.presupuesto * factor * 10000) / 10000,
    real: Math.round(n.real * factor * 10000) / 10000,
    presupuesto_propio: Math.round(n.presupuesto_propio * factor * 10000) / 10000,
    real_propio: Math.round(n.real_propio * factor * 10000) / 10000,
    children: convertida(n.children ?? [], factor)
  }));
}

export function respuestaEdt(metodo: string, ruta: string, cuerpo: unknown, params?: URLSearchParams): unknown | null {
  if (!/\/api\/project\/([^/]+)\/edt\/?$/.test(ruta)) return null;

  /* El proyecto de muestra está en Planificación: es el estado en el que el
     EDT se puede tocar, así que es el único con el que se puede probar. */
  const marco = {
    moneda: 'USD',
    niveles: 3,
    editable: true,
    estado: 'Planificación',
    estados_editables: ['Planificación', 'Replanificación'],
    tipos: { ingreso: 'Ingreso', gasto: 'Gasto' },
    /* Componentes de muestra: en la aplicación real salen del inventario de
       ARMUS por la carga de entidades. */
    componentes: [
      { id_odoo: 8801, descripcion: 'Horas de consultoría', codigo: 'SRV-001', uso: 'ingreso' },
      { id_odoo: 8802, descripcion: 'Viáticos y traslados', codigo: 'SRV-002', uso: 'gasto' },
      { id_odoo: 8803, descripcion: 'Licencias de software', codigo: 'LIC-001', uso: 'gasto' }
    ]
  };

  if (metodo === 'GET') {
    const pedida = (params?.get('moneda') ?? 'USD').toUpperCase();
    const cambio = POR_DOLAR[pedida];

    if (cambio) {
      return {
        success: true, ...marco, moneda: pedida, moneda_edicion: 'USD', convertido: true,
        factor: cambio.factor, cotizacion: { equivalencia: cambio.factor, fecha: cambio.fecha },
        costo_previsto: Math.round(COSTO_PREVISTO * cambio.factor * 100) / 100,
        costo_previsto_moneda_proyecto: COSTO_PREVISTO,
        // MOD aprobada sin elemento (2/11/2026)
        gasto_sin_elemento: { mod: Math.round(12500 * cambio.factor * 100) / 100, otros: 0, total: Math.round(12500 * cambio.factor * 100) / 100 },
        data: convertida(comoRespuesta(arbol), cambio.factor)
      };
    }

    return {
      success: true, ...marco, moneda_edicion: 'USD', convertido: false,
      costo_previsto: COSTO_PREVISTO, costo_previsto_moneda_proyecto: COSTO_PREVISTO, gasto_sin_elemento: { mod: 12500, otros: 0, total: 12500 },
      data: comoRespuesta(arbol)
    };
  }

  if (metodo === 'PUT') {
    // La misma regla que el backend: se guarda en la moneda del proyecto
    const moneda = String((cuerpo as { moneda?: string })?.moneda ?? '').toUpperCase();
    if (moneda && moneda !== 'USD') {
      return { success: false, message: `El EDT se guarda en USD, la moneda del proyecto, y los importes llegaron en ${moneda}. No se guardó nada.` };
    }

    const nuevo = desdeElCuerpo((cuerpo as { items?: any[] })?.items ?? []);

    // La misma regla que el backend
    if (profundidad(nuevo) > 3) {
      return { success: false, message: 'El EDT admite hasta 3 niveles de desglose.' };
    }

    // La misma regla que el backend: si la plata se movió, hay que decir por qué
    const suma = (nodos: NodoDemo[]): number =>
      nodos.reduce((t, n) => t + (n.children.length ? suma(n.children) : n.presupuesto), 0);

    const cambio = Math.round((suma(nuevo) - suma(arbol)) * 100) / 100;
    const motivo = String((cuerpo as { motivo?: string })?.motivo ?? '').trim();

    if (Math.abs(cambio) >= 0.01 && motivo === '') {
      return {
        success: false,
        message: 'Cambiaste el presupuesto del EDT: hace falta decir por qué.',
        necesita_motivo: true
      };
    }

    arbol = nuevo;
    return { success: true, message: 'EDT guardado.', ...marco, data: comoRespuesta(arbol) };
  }

  return null;
}


/**
 * El nombre de un elemento por su id, para que la vista previa resuelva lo
 * mismo que el backend: la provisión guarda **el nombre con el que se imputó**,
 * no una referencia que después haya que ir a buscar.
 *
 * Solo devuelve hojas: un elemento con hijos vale lo que suman sus hijos y no
 * recibe imputaciones.
 */
export function nombreDeElementoEdt(id: number | null | undefined): string | null {
  if (!id) return null;

  const buscar = (nodos: NodoDemo[]): string | null => {
    for (const n of nodos) {
      if (n.id === id) return n.children.length === 0 ? n.descripcion : null;
      const abajo = buscar(n.children);
      if (abajo) return abajo;
    }
    return null;
  };

  return buscar(arbol);
}


/** El número de un elemento por su id, como lo resuelve el backend al leer */
export function numeroDeElementoEdt(id: number | null | undefined): string | null {
  if (!id) return null;

  const buscar = (nodos: NodoDemo[], prefijo: string): string | null => {
    for (let i = 0; i < nodos.length; i++) {
      const numero = prefijo === '' ? String(i + 1) : `${prefijo}.${i + 1}`;
      if (nodos[i].id === id) return numero;
      const abajo = buscar(nodos[i].children, numero);
      if (abajo) return abajo;
    }
    return null;
  };

  return buscar(arbol, '');
}

/**
 * Las hojas de gasto, que son las únicas contra las que se puede planificar.
 *
 * Un elemento con hijos vale lo que suman sus hijos —imputarle horas contaría
 * dos veces— y uno de ingreso es lo que se le vende al cliente. Vive acá y no
 * en los datos del equipo para que la planificación ofrezca **la misma EDT**
 * que muestra la pestaña de EDT del proyecto.
 */
export function hojasDeGastoDemo(): Array<{ id: number; numero: string | null; descripcion: string }> {
  const hojas: Array<{ id: number; numero: string | null; descripcion: string }> = [];

  const recorrer = (nodos: NodoDemo[]): void => {
    for (const n of nodos) {
      if (n.children.length === 0) {
        if (n.tipo === 'gasto') {
          hojas.push({ id: n.id, numero: numeroDeElementoEdt(n.id), descripcion: n.descripcion });
        }
      } else {
        recorrer(n.children);
      }
    }
  };

  recorrer(arbol);

  return hojas;
}
